import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import usersRequest from "../../request/usersRequest";
import { CombinedContext } from "../../context/combinedContext";
import PhoneInput from "../common/phoneInput/phoneInput";
import imageCompression from "browser-image-compression";
import translations from "../../translations/translations";
import companyBasedRequest from "../../request/companyBasedRequest";
import TermsOfServiceModal from "../common/termsOfServiceModal/termsOfServiceModal";
import PasswordField from "../common/passwordField/passwordField";

const RegisterStaff = () => {
    const [searchParams] = useSearchParams();
    const token = searchParams.get("token");
    const navigate = useNavigate();

    const [rememberMe, setRememberMe] = useState(false);

    const [inviteValid, setInviteValid] = useState(false);
    const [verifying, setVerifying] = useState(true);
    const [error, setError] = useState("");
    const [adminId, setAdminId] = useState("");
    const [success, setSuccess] = useState("");
    const [loading, setLoading] = useState(false);

    // Form states
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [phoneNumber, setPhoneNumber] = useState("");
    const [category, setCategory] = useState("");
    const [shopId, setShopId] = useState("");
    const [inviteId, setInviteId] = useState("");
    const [activeModal, setActiveModal] = useState(null);
    const [acceptedTerms, setAcceptedTerms] = useState(false);

    const fileInputRef = useRef(null);
    const maxSlots = 5;

    const { userData, setUserData, language } = useContext(CombinedContext);

    useEffect(() => {
        // if (userData?.user?.role === "admin") return;

        const verifyInvite = async () => {
            try {
                const res = await companyBasedRequest.verifyInvite(token);

                setInviteId(res.inviteId);
                setShopId(res.shopId);
                setFirstName(res.firstName);
                setLastName(res.lastName);
                setEmail(res.email || "");
                setInviteValid(true);

                if (res.adminVerified && !res.used) {
                    if (!userData || !userData.cognitoTokens?.IdToken) {
                        navigate("/signin");
                        return;
                    }
                    setSuccess("Welcome back! Continue registering your shop.");
                }

                // ✅ Mark as verified
                sessionStorage.setItem("inviteVerified", "true");
            } catch (err) {
                setError("Invalid or expired invite link.");
            } finally {
                setVerifying(false);
            }
        };

        verifyInvite();
    }, [token]);

    useEffect(() => {
        if (userData && userData.user.role === "owner" && userData.user.onboardingStatus === "pending-shop") {
            setAdminId(userData.userId);
        }
    }, [userData]);

    if (verifying) {
        return (
            <div className="container">
                <div className="card" style={{ maxWidth: 420, margin: "40px auto", textAlign: "center" }}>
                    <div className="hd">Verifying your invite...</div>
                </div>
            </div>
        );
    }

    if (!inviteValid) {
        return (
            <div className="container">
                <div className="card" style={{ maxWidth: 420, margin: "40px auto", textAlign: "center" }}>
                    <div className="hd">Invite Invalid</div>
                    <p style={{ color: "red" }}>{error}</p>
                </div>
            </div>
        );
    }

    const normalizePhoneNumber = (raw) => {
        if (!raw) return "";
        let cleaned = raw.trim();

        cleaned = cleaned.replace(/[^0-9+]/g, "");

        if (cleaned.startsWith("++")) cleaned = cleaned.replace(/^\++/, "+");

        if (!cleaned.startsWith("+")) cleaned = "+1" + cleaned;

        cleaned = cleaned.replace(/(?!^)\+/g, "");

        if (!/^\+\d{10,15}$/.test(cleaned)) {
            console.warn("⚠️ Invalid phone format:", cleaned);
            return "";
        }

        return cleaned;
    };

    function isStrongPassword(password) {
        return (
            typeof password === "string" &&
            password.length >= 8 &&
            /[A-Z]/.test(password) &&
            /[0-9]/.test(password) &&
            /\./.test(password)
        );
    }

    const handleRegisterAdmin = async () => {
        if (!isStrongPassword(password)) {
            setError(
                language === "es"
                    ? "La contraseña debe tener al menos 8 caracteres, una mayúscula, un número y un punto (.)"
                    : "Password must be at least 8 characters and include an uppercase letter, a number, and a period (.)"
            );
            return;
        }

        if (password !== confirmPassword) {
            setError("Passwords do not match");
            return;
        }
        setError("");
        setLoading(true);

        try {
            const payload = {
                token,
                shopId,
                inviteId,
                email,
                password,
                firstName,
                lastName,
                phone: normalizePhoneNumber(phoneNumber),
                acceptedTermsVersion: "1.0",
            };

            const res = await companyBasedRequest.registerStaff(payload);

            // 🧠 Normalize structure to match login and context
            const formattedData = {
                ...res,
                cognitoTokens: {
                    IdToken: res.cognitoTokens?.IdToken || res.IdToken,
                    AccessToken: res.cognitoTokens?.AccessToken || res.AccessToken,
                    RefreshToken: res.cognitoTokens?.RefreshToken || res.RefreshToken,
                },
            };

            setUserData(formattedData);
            setAdminId(res.adminId);
            navigate("/calendar");

            // 💾 Store tokens & user persistently
            const storage = rememberMe ? localStorage : sessionStorage;
            if (formattedData.cognitoTokens?.IdToken) storage.setItem("idToken", formattedData.cognitoTokens.IdToken);
            if (formattedData.cognitoTokens?.AccessToken)
                storage.setItem("accessToken", formattedData.cognitoTokens.AccessToken);
            if (formattedData.cognitoTokens?.RefreshToken)
                storage.setItem("refreshToken", formattedData.cognitoTokens.RefreshToken);
            storage.setItem("user", JSON.stringify(formattedData.user));
        } catch (err) {
            console.error("Register admin failed:", err);
            setError(err.message || "Failed to register admin.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="container">
            <div className="card" style={{ maxWidth: 420, margin: "40px auto" }}>
                <div className="hd">
                    <div>{translations[language].registerStaff.step1.title}</div>
                </div>

                <div className="bd">
                    {error && (
                        <div className="sub" style={{ color: "red", marginBottom: 10 }}>
                            {error}
                        </div>
                    )}
                    {success && (
                        <div className="sub" style={{ color: "green", marginBottom: 10 }}>
                            {success}
                        </div>
                    )}

                    <>
                        <input className="input" type="email" value={email} disabled style={{ marginBottom: 10 }} />
                        <div className="row" style={{ marginBottom: 10 }}>
                            <input
                                className="input"
                                placeholder={translations[language].registerStaff.step1.firstName}
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                                style={{ marginRight: 5 }}
                            />
                            <input
                                className="input"
                                placeholder={translations[language].registerStaff.step1.lastName}
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </div>
                        <PhoneInput businessPhone={phoneNumber} setBusinessPhone={setPhoneNumber} />
                        <PasswordField
                            value={password}
                            onChange={setPassword}
                            placeholder={translations[language].registerStaff.step1.password}
                            autoComplete="new-password"
                        />

                        <PasswordField
                            value={confirmPassword}
                            onChange={setConfirmPassword}
                            placeholder={translations[language].registerStaff.step1.confirmPassword}
                            autoComplete="new-password"
                        />

                        <div style={{ marginBottom: 10, display: "flex", alignItems: "center" }}>
                            <input
                                type="checkbox"
                                id="rememberMe"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                                style={{ marginRight: 6 }}
                            />
                            <label htmlFor="rememberMe">{translations[language].registerStaff.step1.remember}</label>
                        </div>

                        <div className="terms-checkbox">
                            <label className="terms-label">
                                <input
                                    type="checkbox"
                                    checked={acceptedTerms}
                                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                                />
                                <span>
                                    {translations[language].register.acceptTerms}{" "}
                                    <span className="sf-terms-span" onClick={() => setActiveModal("terms")}>
                                        {translations[language].register.termsLink}
                                    </span>
                                </span>
                            </label>
                        </div>

                        <button
                            className="btn brand"
                            style={{ width: "100%", marginBottom: 10 }}
                            onClick={handleRegisterAdmin}
                            disabled={loading}
                        >
                            {loading
                                ? translations[language].registerStaff.step1.registering
                                : translations[language].registerStaff.step1.register}
                        </button>
                    </>
                </div>
            </div>
            {activeModal === "terms" && <TermsOfServiceModal isOpen onClose={() => setActiveModal(null)} />}
        </div>
    );
};

export default RegisterStaff;
